import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(
    typeof navigator !== 'undefined' && !!navigator.geolocation
  );
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Get user's location on component mount
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationLoading(false);
      },
      (error) => {
        console.log('Geolocation error:', error.message);
        setLocationError('Location access denied. Some features may be limited.');
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password, userLocation?.latitude, userLocation?.longitude);

      if (result.must_change_password) {
        toast.info('Please change your password to continue');
        navigate('/change-password');
      } else {
        toast.success('Logged in successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;

      if (errorDetail?.error === 'geofence_restricted') {
        navigate('/geofence-restricted', {
          state: { message: errorDetail.message }
        });
      } else {
        toast.error(typeof errorDetail === 'string' ? errorDetail : 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img
            src="https://customer-assets.emergentagent.com/job_lbr-build/artifacts/t9f432h8_MB%20Con%20Logo.png"
            alt="MBConstruction"
            className="w-28 h-28 mx-auto mb-2 rounded-2xl object-contain bg-white p-2 shadow-lg"
          />
          <p className="text-slate-400 text-xs mt-1 tracking-wider">Maintenance Management System</p>
        </div>

        <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-8">
          <h2 className="text-xl font-semibold text-slate-800 mb-6 text-center" data-testid="login-heading">
            Sign in to your account
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-slate-700">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-700"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="••••••••"
              />
            </div>

            <button
              data-testid="submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Please wait...' : 'Sign In'}
            </button>

            <p className="text-center text-xs text-slate-500 pt-2" data-testid="invite-only-notice">
              Access is invite-only. Contact your administrator to request an account.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
